import { useEffect, useState } from 'react'

import { biz, type AgingReport, type GstReport } from '../../lib/business'
import { money } from '../../lib/format'

interface SalesReport {
  monthly: Array<{ month: string; invoices: number; revenue: number }>
  document_counts: Record<string, number>
  quotation_conversion_rate: number
}

export default function ReportsPage() {
  const [sales, setSales] = useState<SalesReport | null>(null)
  const [ar, setAr] = useState<AgingReport | null>(null)
  const [ap, setAp] = useState<AgingReport | null>(null)
  const [gst, setGst] = useState<GstReport | null>(null)

  useEffect(() => {
    biz.salesReport(12).then(setSales).catch(() => {})
    biz.receivables().then(setAr).catch(() => {})
    biz.payables().then(setAp).catch(() => {})
    biz.gst().then(setGst).catch(() => {})
  }, [])

  const maxRevenue = Math.max(...(sales?.monthly ?? []).map((m) => m.revenue), 1)

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card">
        <div className="card-head">
          <span className="card-title">Revenue by month</span>
          <div className="grow" />
          {sales && (
            <span className="muted" style={{ fontSize: 12.5 }}>
              quotation conversion{' '}
              <strong style={{ color: 'var(--text)' }}>
                {(sales.quotation_conversion_rate * 100).toFixed(0)}%
              </strong>
            </span>
          )}
        </div>
        <div className="card-body">
          {!sales ? (
            <div className="skeleton" style={{ height: 120 }} />
          ) : sales.monthly.length === 0 ? (
            <div className="muted" style={{ fontSize: 13 }}>
              No invoices yet. Raise one and it will chart here.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              {sales.monthly.map((m) => (
                <div key={m.month} style={{
                  display: 'grid', gridTemplateColumns: '76px 1fr 110px 54px',
                  gap: 10, alignItems: 'center',
                }}>
                  <span className="mono" style={{ fontSize: 12 }}>{m.month}</span>
                  <div className="bar"><i style={{ width: `${(m.revenue / maxRevenue) * 100}%` }} /></div>
                  <span className="num" style={{ fontSize: 12.5 }}>{money(m.revenue)}</span>
                  <span className="muted" style={{ fontSize: 11.5 }}>
                    {m.invoices} inv
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="two-col">
        <Aging title="Receivables — customers owe us" report={ar} rowsKey="by_contact" />
        <Aging title="Payables — we owe vendors" report={ap} rowsKey="by_vendor" />
      </div>

      {gst && (
        <div className="card">
          <div className="card-head">
            <span className="card-title">
              GST — {gst.period.start} to {gst.period.end}
            </span>
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Rate</th><th className="num">Taxable value</th>
                  <th className="num">CGST</th><th className="num">SGST</th>
                  <th className="num">IGST</th><th className="num">Tax</th>
                </tr>
              </thead>
              <tbody>
                {gst.output_tax_by_rate.length === 0 ? (
                  <tr><td colSpan={6} className="muted">Nothing invoiced in this period.</td></tr>
                ) : gst.output_tax_by_rate.map((b) => (
                  <tr key={b.gst_rate}>
                    <td>{b.gst_rate}%</td>
                    <td className="num">{money(b.taxable_value)}</td>
                    <td className="num">{money(b.cgst)}</td>
                    <td className="num">{money(b.sgst)}</td>
                    <td className="num">{money(b.igst)}</td>
                    <td className="num" style={{ fontWeight: 560 }}>{money(b.total_tax)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card-body" style={{ borderTop: '1px solid var(--line)' }}>
            <div className="stat-row">
              <div><div className="stat-label">Output tax</div>
                <div style={{ fontSize: 17, fontWeight: 620 }}>{money(gst.output_tax_total)}</div></div>
              <div><div className="stat-label">Input credit</div>
                <div style={{ fontSize: 17, fontWeight: 620 }}>{money(gst.input_tax_total)}</div>
                <div className="stat-note">from {gst.input_bill_count} parsed bills</div></div>
              <div><div className="stat-label">
                Net {gst.net_payable >= 0 ? 'payable' : 'credit'}</div>
                <div style={{
                  fontSize: 17, fontWeight: 620,
                  color: gst.net_payable >= 0 ? 'var(--warn)' : 'var(--ok)',
                }}>{money(Math.abs(gst.net_payable))}</div></div>
            </div>
            <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
              {gst.note} Filing GSTR-1 / 3B needs the statutory schema and the
              GSTN portal — export this to your accountant.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Aging({ title, report, rowsKey }: {
  title: string
  report: AgingReport | null
  rowsKey: 'by_contact' | 'by_vendor'
}) {
  if (!report) return <div className="skeleton" style={{ height: 220 }} />
  const rows = (report[rowsKey] ?? []) as Array<{
    name: string; outstanding: number; days_overdue: number
  }>

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">{title}</span>
        <div className="grow" />
        <strong style={{ fontSize: 13 }}>{money(report.total_outstanding)}</strong>
      </div>
      {rows.length === 0 ? (
        <div className="empty" style={{ padding: '28px 20px' }}>
          <div className="muted">Nothing outstanding.</div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr><th>Name</th><th className="num">Overdue</th><th className="num">Amount</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name}>
                  <td>{r.name}</td>
                  <td className="num">
                    {r.days_overdue > 0
                      ? <span className="pill pill-warn">{r.days_overdue}d</span>
                      : <span className="muted">—</span>}
                  </td>
                  <td className="num" style={{ fontWeight: 560 }}>{money(r.outstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
